import { useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
  Alert, Button, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import Box from 'elements/Box';
import { ProjectConfig } from 'Global';

type EditorialRow = {
  aniversario_id:number;
  lang_id:number;
  language:string;
  name:string;
  status:string;
};
type EditorialPreview = {
  success:boolean;
  source_sha256:string;
  ready:number;
  items:EditorialRow[];
  writes:number;
};

const descriptions:Record<string,string> = {
  lista_para_importar:'Lista para guardar',
  ya_existe_conservar:'Conservar traducción existente',
  fuente_modificada:'Bloqueada: cambió el español',
  fuente_no_existe:'Bloqueada: ficha no encontrada',
};
const labels:Record<string,string> = {
  en:'Inglés', it:'Italiano', pt:'Portugués',
  fr:'Francés', ca:'Catalán', la:'Latín',
};

export default function AniversariosEditorialImporter() {
  const [file,setFile]=useState<File|null>(null);
  const [preview,setPreview]=useState<EditorialPreview|null>(null);
  const [result,setResult]=useState<{writes:number;skipped:any[]}|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');

  const submit = async (apply:boolean) => {
    if(!file || busy || (apply && !preview)) return;
    if(apply && !window.confirm(
      '¿Guardar solo las traducciones editoriales cuya ficha e idioma estén vacíos? '+
      'No se modificarán textos existentes ni las biografías españolas.'
    )) return;
    setBusy(true);
    setError('');
    try {
      const data=new FormData();
      data.append('editorial_package',file);
      if(apply && preview){
        data.append('confirmed_sha256',preview.source_sha256);
        data.append('confirmed_action','GUARDAR_SOLO_TRADUCCIONES_VACIAS');
      }
      const action=apply
        ? 'apply_aniversarios_editorial_translation_import'
        : 'preview_aniversarios_editorial_translation_import';
      const response=await axios.post(ProjectConfig.api_url,data,{
        params:{request:action},
        headers:{Authorization:'Bearer '+(new Cookies().get('ltkn')||'')},
        timeout:120000,
      });
      const value=response.data;
      if(value?.success!==true){
        throw new Error(value?.error||'La operación no fue aceptada.');
      }
      if(apply) {
        setResult({writes:value.writes||0,skipped:value.skipped||[]});
        setPreview(null);
      } else {
        setPreview(value as EditorialPreview);
        setResult(null);
      }
    } catch (err:any) {
      setError(String(err?.response?.data?.error||err?.message||'Error de importación.').slice(0,400));
    } finally {setBusy(false);}
  };

  return (
    <Box id="aniversarios-editorial-translation-import"
      title="Traducciones editoriales hechas en ChatGPT"
      subtitle="Un solo archivo para varios padres e idiomas · sin proveedores automáticos">
      <Stack spacing={2}>
        <Alert severity="info">
          Esta herramienta únicamente guarda traducciones redactadas en ChatGPT y
          asociadas al identificador y al texto español exacto de cada ficha.
          Comprueba la base de datos en el momento de guardar y no sobrescribe traducciones
          anteriores, incluso si están en un campo estructurado.
        </Alert>
        <input type="file" accept=".json,application/json"
          disabled={busy}
          onChange={e=>{
            setFile(e.target.files?.[0]||null);
            setPreview(null);
            setResult(null);
            setError('');
          }}/>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" disabled={!file||busy}
            onClick={()=>void submit(false)}>
            {busy?'Comprobando…':'Revisar lote editorial'}
          </Button>
          {preview && <Button variant="contained"
            disabled={busy||preview.ready===0}
            onClick={()=>void submit(true)}>
            Guardar {preview.ready} traducciones vacías
          </Button>}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {preview && <>
          <Typography><strong>Traducciones nuevas posibles:</strong> {preview.ready}.
            {' '}<strong>Conservadas o bloqueadas:</strong> {preview.items.length-preview.ready}.
          </Typography>
          <TableContainer sx={{maxHeight:300}}><Table size="small" stickyHeader>
            <TableHead><TableRow>
              <TableCell>Ficha</TableCell>
              <TableCell>Idioma</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {preview.items.map(row=><TableRow key={row.aniversario_id+'-'+row.lang_id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{labels[row.language]||row.language}</TableCell>
                <TableCell>{descriptions[row.status]||row.status}</TableCell>
              </TableRow>)}
            </TableBody>
          </Table></TableContainer>
        </>}
        {result && <Alert severity="success">
          {result.writes} traducciones nuevas guardadas y verificadas en producción.
          {' '}{result.skipped.length} existentes, incompatibles o modificadas se conservaron sin cambio.
          Vuelve a la auditoría para revisar su estado.
        </Alert>}
      </Stack>
    </Box>
  );
}
